import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Icon } from '@/assets/icons/LibrarySvg';
export function PresentationFrameItem({ element, index, total, isSelected, isPresentationMode, onSelect, onSetActiveFrame, onMoveUp, onMoveDown, onReorder, onUpdateName, dragType, }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(element.name || '');
    const inputRef = useRef(null);
    const itemRef = useRef(null);
    const [{ isDragging }, drag] = useDrag({
        type: dragType,
        item: () => ({ id: element.id, originalIndex: index }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (draggedItem, monitor) => {
            if (!monitor.didDrop())
                return;
            const dropResult = monitor.getDropResult();
            if ((dropResult === null || dropResult === void 0 ? void 0 : dropResult.dropIndex) !== undefined &&
                dropResult.dropIndex !== draggedItem.originalIndex) {
                onReorder(draggedItem.originalIndex, dropResult.dropIndex);
            }
        },
    });
    const [{ isOver }, drop] = useDrop({
        accept: dragType,
        hover: (draggedItem, monitor) => {
            if (!itemRef.current || draggedItem.id === element.id)
                return;
            const dragIndex = draggedItem.originalIndex;
            const hoverIndex = index;
            if (dragIndex === hoverIndex)
                return;
            const hoverBoundingRect = itemRef.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset)
                return;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
        },
        drop: () => {
            return { dropIndex: index };
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });
    const dragDropRef = (node) => {
        itemRef.current = node;
        drag(drop(node));
    };
    // Фокусируем инпут при начале редактирования
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);
    // Обновляем editedName при изменении element.name извне
    useEffect(() => {
        if (!isEditing) {
            setEditedName(element.name || '');
        }
    }, [element.name, isEditing]);
    // Прокручиваем к активному фрейму при его активации
    useEffect(() => {
        if (element.isActivePresentationFrame && itemRef.current) {
            itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [element.isActivePresentationFrame]);
    const handleStartEdit = (e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditedName(element.name || '');
    };
    const handleSave = (e) => {
        e.stopPropagation();
        const trimmedName = editedName.trim();
        if (trimmedName !== element.name) {
            onUpdateName(element.id, trimmedName);
        }
        setIsEditing(false);
    };
    const handleCancel = () => {
        setEditedName(element.name || '');
        setIsEditing(false);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave(e);
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };
    const handleBlur = () => {
        handleCancel();
    };
    const displayName = element.name ? `${element.name}` : `Frame #${element.id}`;
    const selectedBackgroundColor = '#525E6ED6';
    const activePresentationFrameBackgroundColor = 'rgba(216, 68, 68, 0.2)';
    const defaultBackgroundColor = '#212325F6';
    const backgroundColor = isDragging
        ? 'rgba(255, 255, 255, 0.4)'
        : isSelected
            ? selectedBackgroundColor
            : isPresentationMode && element.isActivePresentationFrame
                ? activePresentationFrameBackgroundColor
                : defaultBackgroundColor;
    const border = isPresentationMode && element.isActivePresentationFrame
        ? '10px solid rgb(216, 68, 68)'
        : 'none';
    const padding = isPresentationMode && element.isActivePresentationFrame ? '6px 12px 6px 2px' : '6px 12px';
    return (_jsxs("div", { ref: dragDropRef, onClick: () => onSelect(element), style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding,
            borderRadius: 6,
            backgroundColor: isOver ? 'rgba(255, 255, 255, 0.1)' : backgroundColor,
            borderLeft: border,
            opacity: isDragging ? 0.5 : 1,
            cursor: isDragging ? 'grabbing' : 'grab',
        }, children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    maxWidth: '95%',
                    gap: 8,
                    flex: 1,
                }, children: [_jsx("input", { type: "radio", name: "active-presentation-frame", checked: !!element.isActivePresentationFrame, onChange: (e) => {
                            e.stopPropagation();
                            onSetActiveFrame(element);
                        }, title: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0439 \u0444\u0440\u0435\u0439\u043C (\u0431\u0443\u0434\u0435\u0442 \u043F\u043E\u043A\u0430\u0437\u0430\u043D \u0437\u0440\u0438\u0442\u0435\u043B\u044F\u043C)" }), isEditing ? (_jsx("input", { ref: inputRef, type: "text", value: editedName, onChange: (e) => setEditedName(e.target.value), onKeyDown: handleKeyDown, onBlur: handleBlur, onClick: (e) => e.stopPropagation(), style: {
                            flex: 1,
                            padding: '2px 6px',
                            border: '1px solid #fff',
                            borderRadius: 4,
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            fontSize: 'inherit',
                        } })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "item-index", style: { marginRight: 4, color: '#616467', fontSize: '0.9rem' }, children: index + 1 }), _jsx("span", { className: "item-name", style: {
                                    flex: 1,
                                    color: '#eee',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }, children: displayName })] })), !isEditing ? (_jsx("button", { onClick: handleStartEdit, style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2px 4px',
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            color: '#fff',
                            opacity: 0.7,
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.opacity = '1';
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.opacity = '0.7';
                        }, title: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", children: _jsx("div", { style: {
                                width: 14,
                                height: 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: _jsx(Icon, { name: "pencil" }) }) })) : (_jsx("button", { onClick: handleSave, style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2px 4px',
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            color: '#4CAF50',
                        }, title: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", children: _jsx("div", { style: {
                                width: 14,
                                height: 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: _jsx(Icon, { name: "check" }) }) }))] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx("button", { onClick: (e) => {
                            e.stopPropagation();
                            onMoveUp(element);
                        }, disabled: index === 0, style: {
                            padding: '0px 4px',
                            fontSize: '0.9rem',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: index === 0 ? '#494949ff' : '#919191ff',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            color: '#000',
                        }, title: "\u041F\u0435\u0440\u0435\u043C\u0435\u0441\u0442\u0438\u0442\u044C \u0432\u0432\u0435\u0440\u0445", children: "\u2191" }), _jsx("button", { onClick: (e) => {
                            e.stopPropagation();
                            onMoveDown(element);
                        }, disabled: index === total - 1, style: {
                            padding: '0px 4px',
                            fontSize: '0.9rem',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: index === total - 1 ? '#494949ff' : '#919191ff',
                            cursor: index === total - 1 ? 'not-allowed' : 'pointer',
                            color: '#000',
                        }, title: "\u041F\u0435\u0440\u0435\u043C\u0435\u0441\u0442\u0438\u0442\u044C \u0432\u043D\u0438\u0437", children: "\u2193" })] })] }));
}
